import React from 'react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Info, ShieldCheck } from 'lucide-react';
import { Button, ConfirmDialog, showToast } from '@ideoz/ui';
import {
  defaultPermissions,
  enabledAreaCount,
  getIntegration,
  type PermLevel,
} from './data/catalog';
import { useIntegrations } from './state/useIntegrations';
import { StatusBadge } from './components/StatusBadge';
import { PermissionAreaCard } from './components/PermissionAreaCard';

/**
 * Integration detail / permissions page.
 *
 * Connect and disconnect are **real** (OAuth via the broker + backend). The
 * per-area permission controls are a **mockup** — granting/saving them is
 * deferred, so the draft lives only in local state and "Save permissions" is a
 * placeholder. Connect / Disconnect use the transparent (outline) button style.
 */
export function IntegrationDetailModule(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { startupSlug, toolId } = useParams<{ startupSlug: string; toolId: string }>();
  const { getConnection, isConnected, connect, disconnect } = useIntegrations();

  const integration = getIntegration(toolId);
  const backToList = () => navigate(`/${startupSlug}/integrations`);

  // Editable permission draft (mockup only — seeded from catalog defaults).
  const [draft, setDraft] = useState<Record<string, PermLevel>>(() =>
    integration ? defaultPermissions(integration) : {}
  );
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [busy, setBusy] = useState(false);

  const enabled = useMemo(
    () => (integration ? enabledAreaCount(integration, draft) : 0),
    [integration, draft]
  );

  if (!integration) {
    return (
      <section className="mx-auto w-full max-w-3xl px-6 py-16 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('integrations.notFound')}</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={backToList}>
          {t('integrations.backToList')}
        </Button>
      </section>
    );
  }

  const connection = getConnection(integration.id);
  const connected = isConnected(integration.id);
  const Icon = integration.icon;
  const setArea = (areaId: string, level: PermLevel) =>
    setDraft((prev) => ({ ...prev, [areaId]: level }));

  const handleConnect = async () => {
    setBusy(true);
    try {
      // Same-tab navigation to the broker consent flow — we don't return here.
      await connect(integration.id);
    } catch {
      showToast({
        message: t('integrations.toast.connectFailed', { name: integration.name }),
        severity: 'error',
      });
      setBusy(false);
    }
  };

  // Mockup: permissions aren't wired to the backend yet.
  const handleSave = () => {
    showToast({ message: t('integrations.permissionsSaved'), severity: 'success' });
    backToList();
  };

  const handleDisconnect = async () => {
    if (!connection) return;
    setBusy(true);
    try {
      await disconnect(connection.id);
      showToast({
        message: t('integrations.toast.disconnected', { name: integration.name }),
        severity: 'success',
      });
      backToList();
    } catch {
      showToast({
        message: t('integrations.toast.disconnectFailed', { name: integration.name }),
        severity: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mx-auto w-full max-w-3xl px-6 py-10">
      {/* Header */}
      <div className="flex items-start gap-4">
        <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl border border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
          <Icon size={26} />
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
              {integration.name}
            </h1>
            {connected && <StatusBadge connected />}
          </div>
          <p className="mt-1 max-w-xl text-sm text-gray-500 dark:text-gray-400">
            {t(`integrations.catalog.${integration.id}.description`, {
              defaultValue: integration.description,
            })}
          </p>
        </div>
      </div>

      {/* Permissions (mockup) */}
      <div className="mt-8 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            {t('integrations.permissions')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('integrations.areasEnabled', { enabled, total: integration.areas.length })}
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
          <ShieldCheck size={13} />
          {t('integrations.changeAnytime')}
        </span>
      </div>

      <div className="mt-4 flex flex-col gap-3">
        {integration.areas.map((area) => (
          <PermissionAreaCard
            key={area.id}
            area={area}
            value={draft[area.id] ?? area.defaultLevel}
            onChange={(level) => setArea(area.id, level)}
          />
        ))}
      </div>

      <p className="mt-4 flex items-start gap-2 text-xs text-gray-400 dark:text-gray-500">
        <Info size={14} className="mt-0.5 flex-shrink-0" />
        {t('integrations.privacyNote')}
      </p>

      <hr className="my-6 border-gray-200 dark:border-gray-700" />

      {/* Actions */}
      <div className="flex items-center gap-3">
        {connected ? (
          <>
            <Button variant="primary" onClick={handleSave} data-testid="integration-save">
              {t('integrations.savePermissions')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => setConfirmDisconnect(true)}
              disabled={busy}
              size="xs"
              data-testid="integration-disconnect"
            >
              {t('integrations.disconnect')}
            </Button>
          </>
        ) : (
          <Button
            variant="primary"
            onClick={() => void handleConnect()}
            disabled={busy}
            size="xs"
            data-testid="integration-connect"
          >
            {t(`integrations.catalog.${integration.id}.connectLabel`, {
              defaultValue: integration.connectLabel,
            })}
          </Button>
        )}
        <Button variant="ghost" onClick={backToList} data-testid="integration-cancel">
          {t('integrations.cancel')}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmDisconnect}
        variant="danger"
        title={t('integrations.confirm.title', { name: integration.name })}
        description={t('integrations.confirm.desc', { name: integration.name })}
        confirmLabel={t('integrations.disconnect')}
        cancelLabel={t('integrations.cancel')}
        onConfirm={() => {
          setConfirmDisconnect(false);
          void handleDisconnect();
        }}
        onCancel={() => setConfirmDisconnect(false)}
      />
    </section>
  );
}
